import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import { darken, lighten, useTheme } from "@mui/material/styles";
import { ToastBar, Toaster, toast } from "react-hot-toast";

import type { FC, MouseEvent } from "react";

const interactive = (event: MouseEvent<HTMLDivElement>): boolean =>
  event.target instanceof Element && event.target.closest("a, button") !== null;

const dismissOnClick =
  (id: string) =>
  (event: MouseEvent<HTMLDivElement>): void => {
    // Links and buttons inside a toast keep their own behaviour.
    if (interactive(event)) return;
    toast.dismiss(id);
  };

// Dismissing must not move focus or collapse the selection. The editor's
// floating toolbar only exists while its text is selected, so a toast that
// took focus on mousedown would close the toolbar out from under the user.
const keepSelection = (event: MouseEvent<HTMLDivElement>): void => {
  if (interactive(event)) return;
  event.preventDefault();
};

export const AppToaster: FC = () => {
  const theme = useTheme();
  const isLightMode = theme.palette.mode === "light";
  const successBackground =
    theme.vars?.palette?.Alert?.successStandardBg ??
    (isLightMode
      ? lighten(theme.palette.success.light, 0.9)
      : darken(theme.palette.success.light, 0.9));
  const successText =
    theme.vars?.palette?.Alert?.successColor ??
    (isLightMode
      ? darken(theme.palette.success.light, 0.6)
      : lighten(theme.palette.success.light, 0.6));
  const successIcon = (
    <CheckCircleOutlined sx={{ color: theme.palette.success.main, fontSize: 20 }} />
  );
  const errorBackground =
    theme.vars?.palette?.Alert?.errorStandardBg ??
    (isLightMode
      ? lighten(theme.palette.error.light, 0.9)
      : darken(theme.palette.error.light, 0.9));
  const errorText =
    theme.vars?.palette?.Alert?.errorColor ??
    (isLightMode
      ? darken(theme.palette.error.light, 0.6)
      : lighten(theme.palette.error.light, 0.6));
  const errorIcon = <ErrorOutlineOutlined sx={{ color: theme.palette.error.main, fontSize: 20 }} />;

  return (
    <Toaster
      toastOptions={{
        removeDelay: 400,
        success: {
          style: {
            background: successBackground,
            color: successText,
          },
          icon: successIcon,
          iconTheme: {
            primary: successText,
            secondary: successBackground,
          },
        },
        error: {
          style: {
            background: errorBackground,
            color: errorText,
          },
          icon: errorIcon,
          iconTheme: {
            primary: errorText,
            secondary: errorBackground,
          },
        },
      }}
    >
      {/* `toast.custom` toasts never reach this render prop. */}
      {(t) => (
        <div
          role="presentation"
          style={{ display: "contents" }}
          onMouseDown={keepSelection}
          onClick={dismissOnClick(t.id)}
        >
          <ToastBar toast={t} />
        </div>
      )}
    </Toaster>
  );
};
